import React from 'react';
import { DesktopDateTimePicker } from '@mui/lab';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import moment from 'moment';
import { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { granularityOptions, intervalOptions } from '../../../types/app/dashboardHistory.types';
import { dateTimeShowFormat } from '../../../types/core/date.types';
import { useTranslation } from '../../../contexts/translation';
import { AssetDashboardSetting } from '../../../types/app/asset.types';

interface Props {
  defaultDateFrom: Moment;
  defaultDateTo: Moment;
  onChange: (dateFrom: Moment, dateTo: Moment, selectedGranularity: string) => void;
  isState?: boolean;
  settingData?: AssetDashboardSetting;
  setIgnoredStates?: any;
  showGranularity?: boolean;
}

export function DashboardFilter({
  defaultDateFrom,
  defaultDateTo,
  onChange,
  isState,
  settingData,
  setIgnoredStates,
  showGranularity = true,
}: Props) {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<Moment>(defaultDateFrom.clone());
  const [dateTo, setDateTo] = useState<Moment>(defaultDateTo.clone());
  const [selectedInterval, setSelectedInterval] = useState<string>(intervalOptions[0].value);
  const [ignoredSt, setIgnoredSt] = useState<string>('-1,0');
  const [selectedGranularity, setSelectedGranularity] = useState<string>(
    settingData ? settingData.timeAggregation : granularityOptions[0].value,
  );

  const handleChangeIgnoredStates = (stateCodes?: string) => {
    if (stateCodes === '' || stateCodes === undefined) return setIgnoredStates([]);
    // remove whitespaces and split into array
    let str = stateCodes.replace(/ /g, '');
    let arr = str.split(',');
    return setIgnoredStates(arr);
  };

  useEffect(() => {
    let dateFrom, dateTo;
    if (!isState) {
      switch (selectedInterval) {
        case 'today':
          dateFrom = moment().startOf('day');
          dateTo = moment().add(1, 'days').startOf('day');
          break;
        case 'yesterday':
          dateFrom = moment().add(-1, 'days').startOf('day');
          dateTo = moment().startOf('day');
          break;
        case 'lastHours_6':
          dateFrom = moment().add(-6, 'hours').startOf('hour');
          dateTo = moment().add(1, 'hours').startOf('hour');
          break;
        case 'lastHours_12':
          dateFrom = moment().add(-12, 'hours').startOf('hour');
          dateTo = moment().add(1, 'hours').startOf('hour');
          break;
        case 'lastDays_2':
          dateFrom = moment().add(-2, 'days').startOf('day');
          dateTo = moment().add(1, 'days').startOf('day');
          break;
      }
    } else {
      dateFrom = moment().startOf('hour').add(-3, 'hour');
      dateTo = moment().add(1, 'days').startOf('day');
    }

    if (!dateFrom || !dateTo) return;
    setDateFrom(dateFrom.utc());
    setDateTo(dateTo.utc());
    onChange(dateFrom.utc(), dateTo.utc(), selectedGranularity);
  }, [selectedInterval]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item>
          <DesktopDateTimePicker
            label={t('general.inputs.date-from')}
            value={dateFrom.local()}
            ampm={false}
            onChange={() => null}
            onAccept={(date: string | Moment | null) => {
              if (!date) return;
              onChange(moment(date), dateTo, selectedGranularity);
              setDateFrom(moment(date));
            }}
            renderInput={(params: any) => <TextField {...params} />}
          />
        </Grid>
        <Grid item>
          <DesktopDateTimePicker
            label={t('general.inputs.date-to')}
            value={dateTo.local()}
            ampm={false}
            onChange={() => null}
            onAccept={(date: Moment | string | null) => {
              if (!date) return;
              onChange(dateFrom, moment(date), selectedGranularity);
              setDateTo(moment(date));
            }}
            renderInput={(params: any) => <TextField {...params} />}
          />
        </Grid>
        {!isState && (
          <Grid item>
            <FormControl>
              <InputLabel id="demo-simple-select-label">
                {t('general.inputs.dropdown-interval')}
              </InputLabel>
              <Select
                label={t('general.inputs.dropdown-interval')}
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
              >
                {intervalOptions.map((interval: any, index: any) => (
                  <MenuItem value={interval.value} key={index}>
                    {t(interval.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {!isState && showGranularity && (
          <Grid item>
            <FormControl>
              <InputLabel id="demo-simple-select-label">
                {t('general.inputs.dropdown-granularity')}
              </InputLabel>
              <Select
                label={t('general.inputs.dropdown-granularity')}
                value={selectedGranularity}
                onChange={(e) => {
                  setSelectedGranularity(e.target.value);
                  onChange(dateFrom, dateTo, e.target.value);
                }}
              >
                {granularityOptions.map((granularity: any, index: any) => (
                  <MenuItem value={granularity.value} key={index}>
                    {t(granularity.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {isState && (
          <>
            <Grid item xs={12} sm={8} lg={4}>
              <TextField
                fullWidth
                label={t('general.inputs.ignored-states')}
                defaultValue={'-1,0'}
                onChange={(e) => {
                  if (e.target.value === '') setIgnoredSt('');
                  setIgnoredSt(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                sx={{ marginTop: 1 }}
                variant="contained"
                onClick={() => {
                  handleChangeIgnoredStates(ignoredSt);
                }}
              >
                {t('general.action-buttons.apply')}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}
